import React, { useEffect, useCallback, useRef } from 'react';
import { useFormikContext } from 'formik';
import * as Yup from 'yup';
import OSelect from '../../../../Components/OElements/OSelect';

// Workflow Router
import { WorkflowRouter } from '../workflows';
import { residue_config } from '../../../../config';

/**
 * Spec para Formik - initialValues y validationSchema
 */
export const GuideCategoryBarSpec = {
	getInitialValues: () => ({
		guide_category: '',
		epp_operation_type: '',
		residuos_operation_type: '', // NUEVO
		residue_category_id: '', // Para filtrar por categoría específica (Limpieza de Playa)
	}),
	getValidationSchema: () => ({
		guide_category: Yup.string().required('Seleccione categoría de guía'),
		epp_operation_type: Yup.string().when('guide_category', {
			is: 'epp',
			then: schema => schema.required('Seleccione tipo de operación EPP'),
			otherwise: schema => schema.notRequired(),
		}),
		residuos_operation_type: Yup.string().when('guide_category', {
			is: 'residuos',
			then: schema => schema.required('Seleccione tipo de operación'),
			otherwise: schema => schema.notRequired(),
		}),
	}),
	// No genera payload directo al DTE
	toPayload: () => ({}),
};

/**
 * Barra compacta de selección de categoría de guía
 * Se ubica al inicio del formulario y controla el comportamiento de las demás secciones
 */
export const GuideCategoryBar = () => {
	const validation = useFormikContext();
	const prevCategoryRef = useRef(validation.values.guide_category);
	const prevEppTypeRef = useRef(validation.values.epp_operation_type);

	// Obtener tipos de operación desde el workflow
	const eppOperationTypes = WorkflowRouter.getOperationTypes('epp');
	const residuosOperationTypes = WorkflowRouter.getOperationTypes('residuos');

	const isEPP = validation.values.guide_category === 'epp';
	const isResiduos = validation.values.guide_category === 'residuos';

	// Helper: solo setea si el valor realmente cambió (evita re-renders innecesarios)
	const safeSetField = useCallback(
		(field, newValue) => {
			if (validation.values[field] !== newValue) {
				validation.setFieldValue(field, newValue, false);
			}
		},
		[validation]
	);

	// Setear automáticamente residue_type_id y residue_category_id según residuos_operation_type
	useEffect(() => {
		const opType = validation.values.residuos_operation_type;
		if (!opType) return;

		if (opType === 'peligrosos') {
			safeSetField('residue_type_id', residue_config.REACT_APP_RESIDUE_TYPE_PELIGROSOS);
			safeSetField('residue_category_id', '');
		} else if (opType === 'no_peligrosos') {
			safeSetField('residue_type_id', residue_config.REACT_APP_RESIDUE_TYPE_NO_PELIGROSOS);
			safeSetField('residue_category_id', '');
		} else if (opType === 'limpieza_playa') {
			safeSetField('residue_type_id', residue_config.REACT_APP_RESIDUE_TYPE_BEACH_CLEANING);
			safeSetField('residue_category_id', residue_config.REACT_APP_RESIDUE_CATEGORY_BEACH_CLEANING);
		} else if (opType === 'envio_bodega_peligrosos') {
			safeSetField('residue_type_id', residue_config.REACT_APP_RESIDUE_TYPE_PELIGROSOS);
			safeSetField('residue_category_id', '');
		} else if (opType === 'envio_bodega_no_peligrosos') {
			safeSetField('residue_type_id', residue_config.REACT_APP_RESIDUE_TYPE_NO_PELIGROSOS);
			safeSetField('residue_category_id', '');
		}
	}, [validation.values.residuos_operation_type, safeSetField]);

	// Limpiar campos SOLO cuando la categoría realmente cambia (no en mount)
	useEffect(() => {
		const currentCategory = validation.values.guide_category;
		const prevCategory = prevCategoryRef.current;
		prevCategoryRef.current = currentCategory;

		// No limpiar en el primer render
		if (prevCategory === currentCategory) return;

		if (currentCategory !== 'epp' && prevCategory === 'epp') {
			// Salimos de EPP → limpiar campos EPP
			safeSetField('epp_operation_type', '');
			safeSetField('warehouse_origin_id', '');
			safeSetField('warehouse_destination_id', '');
			safeSetField('pick_person_id', '');
			safeSetField('establishment_destination_id', '');
			safeSetField('receptor_rut', '');
			safeSetField('receptor_name', '');
			safeSetField('receptor_establishment_address', '');
			safeSetField('receptor_establishment_commune', '');
			safeSetField('receptor_giro', '');
			safeSetField('receptor_phone', '');
			safeSetField('siep_code_destination', '');
		}
		if (currentCategory !== 'residuos' && prevCategory === 'residuos') {
			// Salimos de Residuos → limpiar campos residuos
			safeSetField('residuos_operation_type', '');
			safeSetField('destiny_type_id', '');
			safeSetField('warehouse_id', '');
			safeSetField('final_destination_id', '');
			safeSetField('residue_type_id', '');
			safeSetField('residue_category_id', '');
			safeSetField('responsible_id', '');
			safeSetField('guide_details', []);
		}
	}, [validation.values.guide_category]);

	// Limpiar destino cuando cambia el tipo de operación EPP
	useEffect(() => {
		const currentEppType = validation.values.epp_operation_type;
		const prevEppType = prevEppTypeRef.current;
		prevEppTypeRef.current = currentEppType;

		// Solo limpiar si realmente cambió (no en mount, no si es vacío)
		if (!currentEppType || prevEppType === currentEppType) return;

		safeSetField('establishment_destination_id', '');
		safeSetField('warehouse_origin_id', '');
		safeSetField('warehouse_destination_id', '');
		safeSetField('pick_person_id', '');
		safeSetField('receptor_rut', '');
		safeSetField('receptor_name', '');
		safeSetField('receptor_establishment_address', '');
		safeSetField('receptor_establishment_commune', '');
		safeSetField('receptor_giro', '');
		safeSetField('receptor_phone', '');
		safeSetField('siep_code_destination', '');
	}, [validation.values.epp_operation_type]);

	return (
		<div
			className='guide-category-bar bg-light rounded-2 mb-3'
			style={{
				padding: '12px 16px',
				borderBottom: '1px solid var(--vz-border-color)',
			}}
		>
			<div className='d-flex align-items-end gap-3 flex-wrap'>
				{/* Selector de Categoría */}
				<div style={{ minWidth: '200px', maxWidth: '240px' }}>
					<OSelect
						label='Categoría de Guía'
						id='guide_category'
						name='guide_category'
						isRequired={true}
						value={validation.values.guide_category || ''}
						itemsSelect={WorkflowRouter.getCategories()}
						validation={validation}
					/>
				</div>

				{/* Selector de Tipo de Operación EPP */}
				{isEPP && (
					<div style={{ minWidth: '200px', maxWidth: '260px' }}>
						<OSelect
							label='Tipo de Operación'
							id='epp_operation_type'
							name='epp_operation_type'
							isRequired={true}
							value={validation.values.epp_operation_type || ''}
							itemsSelect={eppOperationTypes.map(op => ({
								value: op.value,
								label: op.label,
								item: { _id: op.value, name: op.label },
							}))}
							validation={validation}
						/>
					</div>
				)}

				{/* Selector de Tipo de Operación Residuos */}
				{isResiduos && (
					<div style={{ minWidth: '200px', maxWidth: '260px' }}>
						<OSelect
							label='Tipo de Operación'
							id='residuos_operation_type'
							name='residuos_operation_type'
							isRequired={true}
							value={validation.values.residuos_operation_type || ''}
							itemsSelect={residuosOperationTypes.map(op => ({
								value: op.value,
								label: op.label,
								item: { _id: op.value, name: op.label },
							}))}
							validation={validation}
						/>
					</div>
				)}

				{/* Indicador visual del modo seleccionado */}
				{validation.values.guide_category && (
					<div className='ms-auto d-flex align-items-center'>
						<span
							className={`badge ${
								isEPP ? 'bg-primary' : 'bg-success'
							} fs-12`}
						>
							<i
								className={`${
									isEPP ? 'ri-shield-user-line' : 'ri-recycle-line'
								} me-1`}
							></i>
							Guía de{' '}
							{validation.values.guide_category === 'epp'
								? 'EPP'
								: 'Residuos'}
							{isEPP && validation.values.epp_operation_type && (
								<>
									{' - '}
									{validation.values.epp_operation_type === 'retiro'
										? 'Retiro'
										: 'Transferencia'}
								</>
							)}
						{isResiduos && validation.values.residuos_operation_type && (
							<>
								{' - '}
								{validation.values.residuos_operation_type === 'peligrosos'
									? 'Peligrosos'
									: validation.values.residuos_operation_type === 'no_peligrosos'
									? 'No Peligrosos'
									: validation.values.residuos_operation_type === 'envio_bodega_peligrosos'
									? 'Envío desde Bodega - Peligrosos'
									: validation.values.residuos_operation_type === 'envio_bodega_no_peligrosos'
									? 'Envío desde Bodega - No Peligrosos'
									: validation.values.residuos_operation_type === 'limpieza_playa'
									? 'Limpieza de Playa'
									: 'Residuos'}
							</>
						)}
						</span>
					</div>
				)}
			</div>
		</div>
	);
};
